import {
  BackendDeletePayloadTypes,
  BackendDeleteReturnTypes,
  BackendFetchReturnTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  BackendSetPayloadTypes,
  BackendSetReturnTypes,
  BackendUpdatePayloadTypes,
  BackendUpdateReturnTypes,
} from '../backend.model';
import { Collections } from '../collections.model';
import { RecursivePartial } from './helpers';
import { Ordering } from './query';
import { Reference } from './store.model';
import { WebsocketEvent } from './websocket';

/**
 * List of actions corresponding to the root model.
 * Note: this is an extension of @ngrx/store's Action.
 */
export type CollectionActions<K extends keyof Collections> =
  // | { type: 'API_FETCH'; payload: FetchRequestPayload<T> }
  | {
      readonly type: 'API_FETCH_SUCCESS';
      readonly payload: FetchSuccessPayload<K>;
    }
  | {
      readonly type: 'API_FETCH_ERROR';
      readonly payload: FetchErrorPayload<K>;
    }
  | {
      readonly type: 'API_PUSH';
      readonly payload: PushRequestPayload<K>;
    }
  | {
      readonly type: 'API_PUSH_SUCCESS';
      readonly payload: PushSuccessPayload<K>;
    }
  | {
      readonly type: 'API_PUSH_ERROR';
      readonly payload: PushErrorPayload<K>;
    }
  | {
      readonly type: 'API_SET';
      readonly payload: SetRequestPayload<K>;
    }
  | {
      readonly type: 'API_SET_SUCCESS';
      readonly payload: SetSuccessPayload<K>;
    }
  | {
      readonly type: 'API_SET_ERROR';
      readonly payload: SetErrorPayload<K>;
    }
  | {
      readonly type: 'API_UPDATE';
      readonly payload: UpdateRequestPayload<K>;
    }
  | {
      readonly type: 'API_UPDATE_SUCCESS';
      readonly payload: UpdateSuccessPayload<K>;
    }
  | {
      readonly type: 'API_UPDATE_ERROR';
      readonly payload: UpdateErrorPayload<K>;
    }
  | {
      readonly type: 'API_DELETE';
      readonly payload: DeleteRequestPayload<K>;
    }
  | {
      readonly type: 'API_DELETE_SUCCESS';
      readonly payload: DeleteSuccessPayload<K>;
    }
  | {
      readonly type: 'API_DELETE_ERROR';
      readonly payload: DeleteErrorPayload<K>;
    }
  | {
      readonly type: 'REQUEST_DATA';
      readonly payload: RequestDataPayload<K>;
    }
  | {
      readonly type: 'UNKNOWN_API_FETCH_ERROR';
      readonly payload: FetchErrorPayload<K>;
    }
  | {
      readonly type: 'WS_MESSAGE';
      readonly no_persist: boolean;
      readonly payload: WebsocketActionPayload;
    };

/**
 * List of actions for every root model.
 */
export type TypedAction = CollectionActions<keyof Collections>;

interface BasePayload<K extends keyof Collections> {
  readonly type: K; // While this is in the ref, this helps TypeScript do the discriminated union
  readonly ref: Reference<K>;
}

/****************************************
 *  Fetch                               *
 ****************************************/
interface FetchSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly result: BackendFetchReturnTypes[K];
  readonly order: Ordering<K>;
  readonly clientRequestIds: ReadonlyArray<string>;
}

interface FetchErrorPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly order: Ordering<K>;
  readonly clientRequestIds: ReadonlyArray<string>;
}

/****************************************
 *  Push                                *
 ****************************************/
export interface PushRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly document: Collections[K];
  readonly rawRequest: BackendPushPayloadTypes[K];
  readonly asFormData?: boolean;
}

export interface PushSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly document: Collections[K];
  readonly rawRequest: BackendPushPayloadTypes[K];
  readonly result: BackendPushReturnTypes[K];
}

type PushErrorPayload<K extends keyof Collections> = PushRequestPayload<K>;

/****************************************
 *  Set                                 *
 ****************************************/

export interface SetRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly document: Collections[K];
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendSetPayloadTypes[K]; //  FIXME Do I need method?
  readonly asFormData?: boolean;
}

interface SetSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly document: Collections[K];
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendSetPayloadTypes[K]; //  FIXME Do I need method?
  readonly result: BackendSetReturnTypes[K];
}

type SetErrorPayload<K extends keyof Collections> = SetRequestPayload<K>;

/****************************************
 *  Update                                 *
 ****************************************/

export interface UpdateRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly delta: RecursivePartial<Collections[K]>;
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendUpdatePayloadTypes[K]; //  FIXME Do I need method?
  readonly asFormData?: boolean;
}

interface UpdateSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly delta: RecursivePartial<Collections[K]>;
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendUpdatePayloadTypes[K]; //  FIXME Do I need method?
  readonly result: BackendUpdateReturnTypes[K];
}

type UpdateErrorPayload<K extends keyof Collections> = UpdateRequestPayload<K>;

/****************************************
 *  Delete                              *
 ****************************************/

export interface DeleteRequestPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendDeletePayloadTypes[K]; //  FIXME Do I need method?
  readonly asFormData?: boolean;
}

interface DeleteSuccessPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly originalDocument: Collections[K];
  readonly rawRequest: BackendDeletePayloadTypes[K]; //  FIXME Do I need method?
  readonly result: BackendDeleteReturnTypes[K];
}

type DeleteErrorPayload<K extends keyof Collections> = DeleteRequestPayload<K>;

/****************************************
 *  Request data                        *
 ****************************************/

export interface RequestDataPayload<K extends keyof Collections>
  extends BasePayload<K> {
  readonly clientRequestIds: ReadonlyArray<string>;
}

/****************************************
 *  Websocket                           *
 ****************************************/

export type WebsocketActionPayload = WebsocketEvent & {
  readonly toUserId: string;
};

/****************************************
 *  Type guards                         *
 ****************************************/

export function isRequestDataAction(
  a: TypedAction,
): a is {
  readonly type: 'REQUEST_DATA';
  readonly payload: RequestDataPayload<keyof Collections>;
} {
  return a.type === 'REQUEST_DATA';
}

export function isWebsocketAction(
  a: TypedAction,
): a is {
  readonly type: 'WS_MESSAGE';
  readonly no_persist: boolean;
  readonly payload: WebsocketActionPayload;
} {
  return a.type === 'WS_MESSAGE';
}

// Appends the collection key to an action's type in order to clarify what part
// of the store the action is affecting. In the case of a websocket message,
// this appends the type of the message.
// Used by @ngrx/store-devtools
export function datastoreActionSanitizer(
  action: TypedAction,
  id: number,
): { readonly type: string; readonly payload: object } {
  if (!action.payload) {
    return action;
  }

  const ids =
    action.type !== 'WS_MESSAGE' && action.payload.ref.path.ids
      ? action.payload.ref.path.ids.join(', ')
      : undefined;

  return {
    ...action,
    type: `${action.type} [${action.payload.type}] ${ids ? ` - ${ids}` : ''}`,
  };
}
